/**
 * Reusable autocomplete listbox popover.
 *
 * Presentational only — the consumer owns open state, items, filtering,
 * the active index (for keyboard nav), and selection callbacks. The listbox
 * handles:
 *  - Portaling to document.body anchored to a trigger ref (avoids ancestor
 *    overflow clipping).
 *  - Viewport-aware positioning with flip-above when room is tight.
 *  - Auto-scrolling the active item into view.
 *  - Optional empty state and footer slots (e.g. "Press Enter to add 'X'").
 *
 * Used by SelectAutocompleteEditor; designed to be reused by other input
 * controls that need an autocomplete dropdown (table-ref pickers, etc).
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode, RefObject } from 'react';
import { usePopoverPosition } from './usePopoverPosition';
import { splitPopoverStyle, useInPanelScope, usePanelAppearance } from '@/components/properties/panelAppearance';

export interface AutocompleteListboxProps<T> {
  /** Element the popover is anchored to (and matches width to, if requested). */
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  items: T[];
  /** Index of the keyboard-active item (-1 for none). */
  activeIndex: number;
  /** Notify the consumer when hover changes the active row. */
  onActiveIndexChange?: (index: number) => void;
  /** Click-or-Enter selection. */
  onSelect: (item: T, index: number) => void;
  /** Render one row — required so consumers control row markup. */
  renderItem: (
    item: T,
    ctx: { active: boolean; selected: boolean; index: number },
  ) => ReactNode;
  /** Stable react key for each row. */
  getItemKey: (item: T, index: number) => string;
  /** Optional per-item selected predicate (drives `selected` flag in renderItem ctx). */
  isItemSelected?: (item: T, index: number) => boolean;
  /** Marks a row as a non-selectable group header. Headers render as a
   *  div (not a button), don't respond to hover/click, and the consumer
   *  is expected to skip them in its own keyboard navigation. */
  isItemHeader?: (item: T, index: number) => boolean;
  /** Rendered when items is empty. */
  emptyContent?: ReactNode;
  /** Sticky footer slot (e.g. for "Press Enter to add" hint). */
  footer?: ReactNode;
  /** Match trigger width (default true — typical for chip-input dropdowns). */
  matchTriggerWidth?: boolean;
  /** Minimum popover width in pixels. */
  minWidth?: number;
  /** Notify the consumer to close (outside click). Escape is the consumer's job
   *  since the trigger usually owns focus. */
  onRequestClose?: () => void;
  /** Marker attribute the consumer can use for outside-click detection
   *  (e.g. `data-portal-listbox="select-foo"`). */
  dataPortalKey?: string;
  /** DOM id of the listbox, for the combobox's `aria-controls`. */
  id?: string;
}

export function AutocompleteListbox<T>({
  anchorRef,
  open,
  items,
  activeIndex,
  onActiveIndexChange,
  onSelect,
  renderItem,
  getItemKey,
  isItemSelected,
  isItemHeader,
  emptyContent,
  footer,
  matchTriggerWidth = true,
  minWidth = 240,
  onRequestClose,
  dataPortalKey,
  id,
}: AutocompleteListboxProps<T>) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  const inPanel = useInPanelScope();
  const { zoom: panelZoom, contrast } = usePanelAppearance();
  const zoom = inPanel ? panelZoom : 1;

  const style = usePopoverPosition(anchorRef, open, {
    matchTriggerWidth,
    minWidth,
    estimatedHeight: 320,
  });

  // Outside-click → request close. Trigger and the popover itself count as inside.
  useEffect(() => {
    if (!open || !onRequestClose) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      onRequestClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, anchorRef, onRequestClose]);

  // Scroll the active row into view as the user keys through the list.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const el = itemRefs.current.get(activeIndex);
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  if (!open || !style) return null;

  const { shell, content } = splitPopoverStyle(style, zoom);

  return createPortal(
    <div style={shell}>
      <div
        ref={popoverRef}
        id={id}
        className="ac-listbox"
        role="listbox"
        style={content}
        {...(inPanel ? { 'data-pp-contrast': contrast } : {})}
        {...(dataPortalKey ? { 'data-portal-listbox': dataPortalKey } : {})}
      >
        <div className="ac-listbox-scroll">
          {items.length === 0 ? (
            <div className="ac-listbox-empty">{emptyContent ?? 'No matches'}</div>
          ) : (
            items.map((item, i) => {
              const header = isItemHeader?.(item, i) ?? false;
              const active = !header && i === activeIndex;
              const selected = !header && (isItemSelected?.(item, i) ?? false);

              if (header) {
                return (
                  <div // NOSONAR typescript:S6819 accessibility-by-design: role=presentation text row in a listbox: role=presentation keeps this text header from being read as a listbox child that is not an option; <img alt> cannot hold its text
                    key={getItemKey(item, i)}
                    role="presentation"
                    className="ac-listbox-header"
                  >
                    {renderItem(item, { active: false, selected: false, index: i })}
                  </div>
                );
              }

              return (
                <button
                  key={getItemKey(item, i)}
                  ref={(el) => {
                    if (el) itemRefs.current.set(i, el);
                    else itemRefs.current.delete(i);
                  }}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`ac-listbox-item${active ? ' is-active' : ''}${
                    selected ? ' is-selected' : ''
                  }`}
                  onMouseEnter={() => onActiveIndexChange?.(i)}
                  // Use mousedown (not click) so the input doesn't lose focus
                  // before the consumer's selection handler runs.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(item, i);
                  }}
                >
                  {renderItem(item, { active, selected, index: i })}
                </button>
              );
            })
          )}
        </div>
        {footer && <div className="ac-listbox-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export default AutocompleteListbox;
